import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { AppMainService } from 'src/app/app-main.service';
import { AppMainComponent } from 'src/app/app.main.component';
import { WBCommentCategory, WBCreateCommentCategory } from 'src/app/misc/workspace/models/workbook-comment.models';
import { WorkbookCommentService } from 'src/app/misc/workspace/services/workbook-comment.services';

@Component({
  selector: 'app-add-update-comment-category',
  templateUrl: './add-update-comment-category.component.html',
  styleUrl: './add-update-comment-category.component.scss'
})
export class AddUpdateCommentCategoryComponent {

  @Input() displayCreateUpdateCommentCategory: boolean = false;
  @Input() isEditMode: boolean = false; // Determines whether it's Add or Edit mode
  @Input() initialData: any; // Used to populate form in Edit mode
  @Input() showParentCategory: boolean = false;
  @Input() commentCategories: WBCommentCategory[] = [];
  @Output() hideCreateUpdateCategory = new EventEmitter();
  @Output() refreshCategory = new EventEmitter();

  /** Subscription and Cleanup */
  protected ngUnsubscribe$: Subject<void> = new Subject<void>(); 

  commentCategoryForm!: FormGroup;
  constructor(
    private readonly app: AppMainComponent,
    public readonly workbookCommentService: WorkbookCommentService,
    private readonly appMainService: AppMainService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private fb: FormBuilder
  ) { }


  ngOnInit(): void {
    this.initializeForm();
    if(this.commentCategories.length === 0 && !this.isEditMode){
      this.getWBCommentCategory();
    }
    if (this.isEditMode && this.initialData) {
      this.populateForm(this.initialData);
    }
  }

  initializeForm(): void {
    this.commentCategoryForm = this.fb.group({
      name: ['', Validators.required],
      color: ['#ff0000', Validators.required], // Default color set to red
      parentId: [null] // Optional dropdown control
    });
  }

  populateForm(data: any): void {
    this.commentCategoryForm.patchValue({
      name: data.name,
      color: data.color,
      parentId: data.parentId || null
    });
  }

  createUpdateCommentCategory(formValue: any): void {
    if (this.commentCategoryForm.invalid) {
      this.commentCategoryForm.markAllAsTouched(); // Mark all controls as touched
      return; // Exit the function if the form is invalid
    }
    if (this.isEditMode) {
    const wbUpdateCommentCategory = {
      id: this.initialData.id,
      name: formValue.name,
      color: formValue.color,
      parentId: formValue.parentId ? formValue.parentId : null
    }
    this.updateCommentCategory(wbUpdateCommentCategory);
  } else {
      // Handle create logic
      const wbCreateCommentCategory: WBCreateCommentCategory = {
        name: formValue.name,
        color: formValue.color,
        parentId: formValue.parentId ? formValue.parentId : null,
      }
      this.createCommentCategory(wbCreateCommentCategory);
    }
    this.displayCreateUpdateCommentCategory = false;
  }

  createCommentCategory(wbCreateCommentCategory: WBCreateCommentCategory) {    
    this.appMainService.isShowSpinner('createCommentCategory');
    this.workbookCommentService.createCommentCategory(wbCreateCommentCategory).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('createCommentCategory');
        this.messageService.add({severity:'success', summary:'Success', detail:'Comment Category created successfully'});
        this.refreshCategory.emit(true);
        this.onHideCategoryModal();
      },
      error: (error) => {
        this.appMainService.isHideSpinner('createCommentCategory');
        this.messageService.add({severity:'error', summary:'Error', detail:  error?.error?.Message || 'Failed to create Comment Category'});
      }
    })        
  }

  updateCommentCategory(wbUpdateCommentCategory: any) {
    this.appMainService.isShowSpinner('updateCommentCategory');
    this.workbookCommentService.updateCommentCategory(wbUpdateCommentCategory)
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe({
            next: (response: any) => {
                this.appMainService.isHideSpinner('updateCommentCategory');
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Comment Category updated successfully' });
                this.refreshCategory.emit(true);
                this.onHideCategoryModal();
            },
            error: (error) => {
                this.appMainService.isHideSpinner('updateCommentCategory');
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to update Comment Category' });
            }
        });
}

  onHideCategoryModal(): void {
    this.commentCategoryForm.reset();
    this.displayCreateUpdateCommentCategory = false;
    this.hideCreateUpdateCategory.emit({ "displayCreateUpdateCommentCategory": this.displayCreateUpdateCommentCategory});
  }

  getWBCommentCategory() {
    this.appMainService.isShowSpinner('getWBCommentCategory');
    this.workbookCommentService.getCommentCategory().pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('getWBCommentCategory');
        this.commentCategories = response;
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getWBCommentCategory');
        this.commentCategories = [];
      }
    })
  }
  ngOnDestroy() {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }  
}
